package com.neurobs.react

import slinky.core.{FunctionalComponent, KeyAddingStage}
import slinky.core.annotations.react
import slinky.core.facade.ReactElement
import slinky.web.html.{button, className, disabled, div, key, onClick, span, style}

object DialogResult extends Enumeration {
	val Ok, Yes, No, Cancel, Continue, Save, Delete, Custom = Value
}

case class DialogButton(caption: String, result: DialogResult.Value, disabled: Boolean = false)

/**
 * Displays a dialog form with many optional elements.
 */
@react object Dialog {
	/**
	 * @param title title centered at the top
	 * @param description Text displayed under the title
	 * @param custom custom content displayed under the description
	 * @param error Error message displayed above the buttons
	 * @param status Status message displayed above the buttons
	 * @param emWidth em width of the form
	 * @param buttons Buttons displayed acrose the bottom
	 * @param onClick Called when a buttons is clicked
	 */
	case class Props
	(
		title: Option[String] = None,
		description: Option[String] = None,
		custom: Option[ReactElement] = None,
		error: Option[String] = None,
		status: Option[String] = None,
		emWidth: Option[Double] = None,
		buttons: Either[Seq[DialogResult.Value], Seq[DialogButton]],
		onClick: DialogButton => Unit
	)

	val component = FunctionalComponent[Props] { props =>
		val buttons = props.buttons match {
			case Left(values) => values.map(v => DialogButton(v.toString, v))
			case Right(buttons) => buttons
		}
		val styles = props.emWidth match {
			case Some(w) => StyleUtil.makeStyle(("width", s"${w}em"), ("display", "inline-block"))
			case None => StyleUtil.makeStyle(("display", "inline-block"))
		}
		div(style := styles)(
			LayoutTable(gapSize = GapSize.Large, fullWidth = true)(
				props.title.map(t => LTFullRow(HoriAlign.Left)(div(className := titleClass)(t)).withKey(key = "title")),
				props.description.map(d => LTFullRow(HoriAlign.Left)(d).withKey(key = "desc")),
				props.custom.map(c => LTFullRow(HoriAlign.Left)(c).withKey(key = "cust")),
				props.status.map(s => LTFullRow(HoriAlign.Center)(span(className := statusClass)(s)).withKey(key = "status")),
				props.error.map(e => LTFullRow(HoriAlign.Center)(span(className := errorClass)(e)).withKey(key = "error")),
				LTFullRow(HoriAlign.Center)(
					LayoutTable(gapSize = GapSize.Medium, fullWidth = false)(
						LTRow(
							buttons map { b =>
								LTCell(HoriAlign.Left)(
									Button(caption = b.caption, disabled = b.disabled, onClick = { () => props.onClick(b)})
								).withKey(b.caption)
							}
						).withKey(key = "0")
					)
				).withKey(key = "buttons")
			)
		)
	}

	private val titleClass = "dlg-title"
	private val errorClass = "structure-status-error"
	private val statusClass = "structure-status-progress"

	def simpleDialog(title: String, description: String, emWidth: Option[Double] = None, onClick: () => Unit): KeyAddingStage = {
		ModalContainer(
			Dialog(
				title = Some(title),
				description = Some(description),
				emWidth = emWidth,
				buttons = Left(Seq(DialogResult.Continue)),
				onClick = _ => onClick()
			)
		)
	}

	def yesNoDialog(title: String, yesNoQuestion: String, emWidth: Option[Double] = None, onClick: Boolean => Unit): KeyAddingStage = {
		ModalContainer(
			Dialog(
				title = Some(title),
				description = Some(yesNoQuestion),
				emWidth = emWidth,
				buttons = Left(Seq(DialogResult.No, DialogResult.Yes)),
				onClick = res => onClick(res.result == DialogResult.Yes)
			)
		)
	}
}
